import type React from 'react';
import { type Dispatch, type SetStateAction, useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { debounce } from 'lodash';

import { lessonToWorkout } from 'app/utils/workouts';
import type { WorkoutListWrapperQuery, WorkoutListWrapperQueryVariables, LessonCondition } from 'app/types/graphql';
import { WorkoutListWrapper as WORKOUTS } from 'app/ui/components/molecules/workout-list-wrapper/workouts.gql';

import WorkoutList, { type Workout } from 'app/ui/components/molecules/workout-list';
import WorkoutFilter from 'app/ui/components/molecules/workout-filter';

import type { Pills } from 'app/ui/components/molecules/pill-field-set';
import { AVAILABLE_DURATION_DATA, type DurationData } from 'app/ui/components/molecules/workout-list-controller';

import { minutesToSeconds } from 'app/utils/duration-handler';
import { trackEvent } from 'src/app/utils/analytics/tracker';
import { parseFiltersByLabels } from 'src/app/utils/analytics/amplitude/helpers';

const BATCH_SIZE = 15;

type SelectedFilters = {
  concept: string | null;
  equipment: string[];
  intensity: number | null;
  duration: DurationData['id'] | null;
};

type SelectedFiltersHandlers = {
  concept: (value: string) => void;
  equipment: (value: string) => void;
  duration: (value: string) => void;
  intensity: Dispatch<SetStateAction<number | null>>;
};

type FilterPills = {
  concept: Pills;
  equipment: Pills;
  duration: Pills;
};

type ManageMoreFilters = {
  showButton: boolean;
  showSidebar: boolean;
  setSidebar: (value: boolean) => void;
  reset: () => void;
};

type Props = {
  selectedFilters: SelectedFilters;
  selectedFiltersHandlers: SelectedFiltersHandlers;
  filterPills: FilterPills;
  manageMoreFilters: ManageMoreFilters;
};

export const getWorkoutDurationRange = (selectedDuration: DurationData['id']) => {
  const DEFAULT_RANGE = { min: 0, max: minutesToSeconds(120) };

  const durationRange = AVAILABLE_DURATION_DATA.find((element) => element.id === selectedDuration);

  if (durationRange) {
    return {
      ...(durationRange.min ? { min: minutesToSeconds(durationRange.min) } : {}),
      ...(durationRange.max ? { max: minutesToSeconds(durationRange.max) } : {}),
    };
  }
  return DEFAULT_RANGE;
};

const WorkoutListWrapper: React.FC<Props> = ({
  selectedFilters,
  selectedFiltersHandlers,
  filterPills,
  manageMoreFilters,
}) => {
  const [offset, setOffset] = useState<number>(0);
  const [accumulatedWorkouts, setAccumulatedWorkouts] = useState<Workout[]>([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const previousFiltersRef = useRef<string>('');

  const lessonsCondition: LessonCondition = useMemo(
    () => ({
      hasConcept: true,
      ...(selectedFilters.concept && selectedFilters.concept !== 'all-concept'
        ? { concept: { id: selectedFilters.concept } }
        : {}),
      ...(selectedFilters.intensity ? { intensity: selectedFilters.intensity } : {}),
      ...(selectedFilters.equipment.length > 0 && !selectedFilters.equipment.includes('all-equipment')
        ? { equipment: selectedFilters.equipment.map((id) => ({ id: Number(id) })) }
        : {}),
      ...(selectedFilters.duration ? { durationRange: getWorkoutDurationRange(selectedFilters.duration) } : {}),
    }),
    [selectedFilters.concept, selectedFilters.intensity, selectedFilters.equipment, selectedFilters.duration],
  );

  const queryVariables: WorkoutListWrapperQueryVariables = {
    lessonsCondition,
    filters: {
      first: BATCH_SIZE,
      offset,
    },
  };

  const { data, loading } = useQuery<WorkoutListWrapperQuery, WorkoutListWrapperQueryVariables>(WORKOUTS, {
    variables: queryVariables,
    fetchPolicy: 'no-cache',
  });

  // Reset pagination when filters change
  useEffect(() => {
    const currentFiltersKey = JSON.stringify(selectedFilters);
    if (previousFiltersRef.current !== currentFiltersKey) {
      previousFiltersRef.current = currentFiltersKey;
      setOffset(0);
      setAccumulatedWorkouts([]);
      setHasMore(true);
    }
  }, [selectedFilters]);

  // Accumulate workouts when new data arrives
  useEffect(() => {
    if (data?.lessons?.nodes) {
      const newWorkouts = data.lessons.nodes.map((lesson) => lessonToWorkout(lesson));

      if (offset === 0) {
        setAccumulatedWorkouts(newWorkouts);
      } else {
        setAccumulatedWorkouts((prev) => [...prev, ...newWorkouts]);
      }

      setLoadingMore(false);

      const totalCount = data.lessons.totalCount || 0;
      const currentTotal = offset + newWorkouts.length;
      setHasMore(currentTotal < totalCount);
    }
  }, [data, offset]);

  // Track filter events
  useEffect(() => {
    if (Object.keys(lessonsCondition).length > 0 && data?.lessons?.nodes && offset === 0) {
      const parsedFilters = parseFiltersByLabels(lessonsCondition, filterPills);
      trackEvent('workout_filtered', {
        filters_applied: parsedFilters,
        results_count: data?.lessons?.totalCount,
      });
    }
  }, [data, lessonsCondition, filterPills, offset]);

  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore || loading) {
      return;
    }

    setLoadingMore(true);
    setOffset((prevOffset) => prevOffset + BATCH_SIZE);
  }, [loadingMore, hasMore, loading]);

  const loadMoreRef = useRef(loadMore);
  loadMoreRef.current = loadMore;

  const debouncedHandleScroll = useMemo(
    () =>
      debounce(() => {
        const scrollTop = window.innerHeight + window.scrollY;
        const documentHeight = document.documentElement.offsetHeight;

        if (scrollTop >= documentHeight - 100) {
          loadMoreRef.current();
        }
      }, 200),
    [],
  );

  useEffect(() => {
    window.addEventListener('scroll', debouncedHandleScroll);
    return () => window.removeEventListener('scroll', debouncedHandleScroll);
  }, [debouncedHandleScroll]);

  const isInitialLoading = loading && accumulatedWorkouts.length === 0;

  return (
    <>
      <WorkoutList
        workouts={accumulatedWorkouts}
        isLoading={isInitialLoading}
        isLoadingMore={loadingMore}
        handleConceptChange={selectedFiltersHandlers.concept}
        conceptFilters={filterPills.concept}
        setShowMoreFilters={manageMoreFilters.setSidebar}
        allowMoreFilters={manageMoreFilters.showButton}
        largeTitle={true}
      />
      {manageMoreFilters.showSidebar && (
        <WorkoutFilter
          equipmentFilters={filterPills.equipment}
          durationFilters={filterPills.duration}
          conceptFilters={filterPills.concept}
          intensityValue={selectedFilters.intensity}
          handleEquipmentChange={selectedFiltersHandlers.equipment}
          handleIntensityChange={selectedFiltersHandlers.intensity}
          handleDurationChange={selectedFiltersHandlers.duration}
          handleConceptChange={selectedFiltersHandlers.concept}
          setShowMoreFilters={manageMoreFilters.setSidebar}
          resetFilters={manageMoreFilters.reset}
        />
      )}
    </>
  );
};

export default WorkoutListWrapper;
